/**
 * KarmanaRegistrationForm
 *
 * Layout: each field is a two-column row — label (left) | input/controls (right).
 * Phone: +91 prefix shown non-editably; 10-digit validation.
 * Email: format validation.
 * Auto-fill: applied via useEffect when user auth resolves.
 *
 * Non-signed users: registration is saved immediately on submit (status=confirmed).
 * OTP is then triggered for account activation only, via the `onOTPRequired` callback.
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { registerForActivity, registerAnonymous, uploadRegistrationImage } from '../api/api';
import { Loader, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { track } from '../utils/analytics';
import { SafeHtml } from './SafeHtml';

// ── Shared input styles ────────────────────────────────────────────────────────
const inp = (hasError) =>
  `w-full px-4 py-2.5 rounded-xl border text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${
    hasError ? 'border-red-400 bg-red-50' : 'border-gray-200'
  }`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const KARMANA_PLACEHOLDER = 'karmana user';

const isPlaceholder = (name) =>
  !name || name.toLowerCase().trim() === KARMANA_PLACEHOLDER;

// Strip +91 prefix so we only store the 10-digit number in local state
function stripCountryCode(phone) {
  if (!phone) return '';
  if (phone.startsWith('+91')) return phone.slice(3);
  if (phone.startsWith('91') && phone.length === 12) return phone.slice(2);
  return phone;
}

function buildInitValues(fields, user) {
  if (!user) return {};
  const pre = {};
  if (fields.find(f => f.id === 'default_name') && !isPlaceholder(user.name))
    pre['default_name'] = user.name || '';
  if (fields.find(f => f.id === 'default_email'))
    pre['default_email'] = user.email || '';
  if (fields.find(f => f.id === 'default_phone'))
    pre['default_phone'] = stripCountryCode(user.phone || '');
  return pre;
}

function buildResponses(fields, values) {
  return fields.map(f => ({
    field_id: f.id,
    field_name: f.name,
    value: values[f.id] || '',
  }));
}

export default function KarmanaRegistrationForm({
  activityId,
  fields,
  user,
  registrationRequirement,
  onSuccess,
  onOTPRequired,   // fn({ method, email, pendingRegId, confirmResult }) — for anon users
  sourceProps = {},   // { activity_id, activity_name, organisation_id, organisation_name, source_page, activity_position }
  onFieldsChange,     // (filledCount, totalCount) => void — for parent abandonment tracking
  disabled = false,   // true after activity date has passed
  registrationFull = false, // true when capacity is reached
}) {
  const [values, setValues]         = useState({});
  const [touched, setTouched]       = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading]   = useState({});
  const trackedFieldsRef            = useRef(new Set()); // tracks fields touched for first-interaction event

  // ── Helper: notify parent of filled-count changes ──────────────────────────
  const _notifyFieldsChange = (newValues) => {
    if (!onFieldsChange) return;
    const filled = fields.filter(f => {
      const v = newValues[f.id];
      return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
    }).length;
    onFieldsChange(filled, fields.length);
  };

  // ── Helper: first-touch field interaction tracking ─────────────────────────
  const _trackFieldInteraction = (id) => {
    if (trackedFieldsRef.current.has(id)) return;
    trackedFieldsRef.current.add(id);
    const fieldIndex = fields.findIndex(f => f.id === id);
    const field = fields.find(f => f.id === id);
    const prefillInit = buildInitValues(fields, user);
    track('karmana_reg_field_interaction', {
      activity_id: sourceProps?.activity_id || activityId,
      source_page: sourceProps?.source_page || null,
      field_id: id,
      field_type: field?.type || 'text',
      is_required: field?.mandatory ?? false,
      is_prefilled: !!(prefillInit && prefillInit[id]),
      field_index_in_form: fieldIndex,
    });
  };

  // Apply auto-fill from user profile once auth resolves
  useEffect(() => {
    if (!user) return;
    const init = buildInitValues(fields, user);
    setValues(prev => {
      const patch = {};
      Object.keys(init).forEach(k => { if (!prev[k]) patch[k] = init[k]; });
      return Object.keys(patch).length ? { ...prev, ...patch } : prev;
    });
  }, [user]); // eslint-disable-line

  // Track form opened once on mount
  useEffect(() => {
    track('karmana_reg_form_opened', {
      activity_id: sourceProps?.activity_id || activityId,
      activity_name: sourceProps?.activity_name || null,
      organisation_id: sourceProps?.organisation_id || null,
      source_page: sourceProps?.source_page || null,
      activity_position: sourceProps?.activity_position ?? null,
      is_authenticated: !!user,
      field_count: fields.length,
      required_field_count: fields.filter(f => f.mandatory).length,
    });
  }, []); // eslint-disable-line

  const recaptchaRef          = useRef(null);
  const recaptchaContainerRef = useRef(null);

  const set = (id, v) => {
    const newValues = { ...values, [id]: v };
    setValues(newValues);
    setTouched(prev => ({ ...prev, [id]: true }));
    _trackFieldInteraction(id);
    _notifyFieldsChange(newValues);
  };

  // ── Image upload for image_upload fields ──────────────────────────────────────
  const handleImageUpload = async (fieldId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) { toast.error('Only JPG, PNG, and WebP images are allowed'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5 MB'); return; }
    setUploading(prev => ({ ...prev, [fieldId]: true }));
    try {
      const result = await uploadRegistrationImage(file, activityId);
      const url = result.data?.url || result.data?.file_url || '';
      if (url) { set(fieldId, url); }
      else toast.error('Upload failed — no URL returned');
    } catch { toast.error('Upload failed. Please try again.'); }
    finally { setUploading(prev => ({ ...prev, [fieldId]: false })); }
  };

  // ── Validation ───────────────────────────────────────────────────────────────
  const getErrors = useCallback(() => {
    const errs = {};
    fields.forEach(f => {
      const val = values[f.id];

      // Phone: 10-digit validation
      if (f.id === 'default_phone') {
        const digits = (val || '').replace(/\D/g, '');
        if (f.mandatory && digits.length !== 10)
          errs[f.id] = 'Enter a valid 10-digit number';
        else if (!f.mandatory && digits.length > 0 && digits.length !== 10)
          errs[f.id] = 'Enter a valid 10-digit number';
        return;
      }

      // Email: format validation
      if (f.id === 'default_email') {
        const email = (val || '').trim();
        if (f.mandatory && !EMAIL_RE.test(email))
          errs[f.id] = 'Enter a valid email address';
        else if (!f.mandatory && email && !EMAIL_RE.test(email))
          errs[f.id] = 'Enter a valid email address';
        return;
      }

      // Other mandatory fields
      if (f.mandatory) {
        if (f.type === 'checkbox') {
          if (!Array.isArray(val) || val.length === 0) errs[f.id] = 'Required';
        } else if (f.type === 'image_upload') {
          if (!val || typeof val !== 'string' || !val.trim()) errs[f.id] = 'Required — please upload an image';
        } else if (!val || (typeof val === 'string' && !val.trim())) {
          errs[f.id] = 'Required';
        }
      }
    });

    // Anonymous: must have at least email or phone
    if (!user) {
      const email = values['default_email'] || '';
      const phone = values['default_phone'] || '';
      if (!email.trim() && !phone.trim())
        errs['__contact'] = 'Please provide an email or phone number';
    }

    return errs;
  }, [fields, values, user]);

  // ── Checkbox helper ───────────────────────────────────────────────────────────
  const handleCheckbox = (fieldId, optVal, checked) => {
    setValues(prev => {
      const current = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
      const newValues = { ...prev, [fieldId]: checked ? [...current, optVal] : current.filter(v => v !== optVal) };
      _notifyFieldsChange(newValues);
      return newValues;
    });
    setTouched(prev => ({ ...prev, [fieldId]: true }));
    _trackFieldInteraction(fieldId);
  };

  // ── Firebase phone setup ──────────────────────────────────────────────────────
  const setupRecaptcha = useCallback(phone => {
    if (!recaptchaRef.current && recaptchaContainerRef.current) {
      recaptchaRef.current = new RecaptchaVerifier(auth, recaptchaContainerRef.current, { size: 'invisible' });
    }
    return signInWithPhoneNumber(auth, phone, recaptchaRef.current);
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const allTouched = {};
    fields.forEach(f => { allTouched[f.id] = true; });
    setTouched({ ...allTouched, __contact: true });

    const errs = getErrors();
    const filledCount = fields.filter(f => {
      const v = values[f.id];
      return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
    }).length;

    // Track submit click (before any blocking)
    track('karmana_reg_submit_click', {
      activity_id: sourceProps?.activity_id || activityId,
      source_page: sourceProps?.source_page || null,
      fields_filled: filledCount,
      total_fields: fields.length,
      has_email: !!(values['default_email'] || '').trim(),
      has_phone: !!(values['default_phone'] || '').trim(),
      has_validation_errors: Object.keys(errs).length > 0,
    });

    if (Object.keys(errs).length > 0) {
      track('karmana_reg_submit_failed', {
        activity_id: sourceProps?.activity_id || activityId,
        error_type: 'validation',
        fields_with_errors: Object.keys(errs),
      });
      return;
    }

    setSubmitting(true);
    try {
      const responses = buildResponses(fields, values);
      const phoneVal = values['default_phone'] || '';
      const phoneE164 = phoneVal ? (phoneVal.startsWith('+') ? phoneVal : `+91${phoneVal}`) : null;

      const payload = {
        responses,
        name:  values['default_name']  || null,
        email: values['default_email'] || null,
        phone: phoneE164,
      };

      if (user) {
        // Signed-in: direct registration — no OTP
        const res = await registerForActivity(activityId, payload);
        const alreadyReg = res.data.status === 'already_registered';
        track('karmana_reg_success', {
          activity_id: sourceProps?.activity_id || activityId,
          is_authenticated: true,
          already_registered: alreadyReg,
        });
        onSuccess({ alreadyRegistered: alreadyReg });
      } else {
        // Anonymous: registration is saved immediately; OTP is for account activation only
        const res = await registerAnonymous(activityId, payload);
        if (res.data.status === 'already_registered') {
          track('karmana_reg_success', {
            activity_id: sourceProps?.activity_id || activityId,
            is_authenticated: false,
            already_registered: true,
          });
          onSuccess({ alreadyRegistered: true });
          return;
        }

        const method = res.data.method;
        const pendingId = res.data.pending_registration_id;
        const emailVal = values['default_email'] || '';

        track('karmana_reg_success', {
          activity_id: sourceProps?.activity_id || activityId,
          is_authenticated: false,
          already_registered: false,
          otp_method: method,
        });
        onSuccess({ newUser: true });

        // Trigger OTP flow for account activation (non-blocking for the registration)
        if (method === 'phone' && phoneE164) {
          setupRecaptcha(phoneE164)
            .then(confirmResult => {
              onOTPRequired?.({ method: 'phone', email: emailVal, pendingRegId: pendingId, confirmResult });
            })
            .catch(() => toast.error('Registration saved! Phone verification failed — you can verify later.'));
        } else if (method === 'email' && emailVal) {
          onOTPRequired?.({ method: 'email', email: emailVal, pendingRegId: pendingId, confirmResult: null });
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const errs = getErrors();
  const showErr = id => touched[id] && errs[id];

  // ── Render a single field's input control ─────────────────────────────────────
  const renderControl = (field) => {
    const hasErr = !!showErr(field.id);

    // Phone field has special +91 prefix treatment
    if (field.id === 'default_phone') {
      return (
        <div>
          <div className={`flex items-stretch overflow-hidden rounded-xl border ${hasErr ? 'border-red-400' : 'border-gray-200'} bg-white focus-within:ring-2 focus-within:ring-green-500 focus-within:border-transparent transition-colors`}>
            <span className="flex items-center px-3 bg-gray-100 border-r border-gray-200 text-sm font-medium text-gray-500 select-none whitespace-nowrap">
              +91
            </span>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="What is Your Phone No"
              value={values['default_phone'] || ''}
              onChange={e => set('default_phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              className="flex-1 px-3 py-2.5 text-sm bg-white focus:outline-none"
              disabled={disabled}
              data-testid="reg-input-default_phone"
            />
          </div>
          {hasErr && <p className="text-xs text-red-500 mt-1">{errs[field.id]}</p>}
        </div>
      );
    }

    const fieldType = field.type || 'text';

    // Name field
    if (field.id === 'default_name') {
      return (
        <div>
          <input
            className={inp(hasErr)}
            placeholder="What is Your Name"
            value={values[field.id] || ''}
            onChange={e => set(field.id, e.target.value)}
            maxLength={100}
            disabled={disabled}
            data-testid={`reg-input-${field.id}`}
          />
          {hasErr && <p className="text-xs text-red-500 mt-1">{errs[field.id]}</p>}
        </div>
      );
    }

    // Email field
    if (field.id === 'default_email') {
      return (
        <div>
          <input
            className={inp(hasErr)}
            placeholder="What is Your Email Id"
            value={values[field.id] || ''}
            onChange={e => set(field.id, e.target.value)}
            maxLength={200}
            disabled={disabled}
            data-testid={`reg-input-${field.id}`}
          />
          {hasErr && <p className="text-xs text-red-500 mt-1">{errs[field.id]}</p>}
        </div>
      );
    }

    if (fieldType === 'text') {
      return (
        <div>
          <input
            className={inp(hasErr)}
            placeholder="Answer"
            value={values[field.id] || ''}
            onChange={e => set(field.id, e.target.value)}
            maxLength={100}
            disabled={disabled}
            data-testid={`reg-input-${field.id}`}
          />
          {hasErr && <p className="text-xs text-red-500 mt-1">{errs[field.id]}</p>}
        </div>
      );
    }

    if (fieldType === 'paragraph') {
      return (
        <div>
          <textarea
            className={inp(hasErr).replace('py-2.5', 'py-2') + ' resize-none'}
            placeholder="Answer"
            value={values[field.id] || ''}
            onChange={e => set(field.id, e.target.value)}
            rows={2}
            maxLength={2000}
            disabled={disabled}
            data-testid={`reg-textarea-${field.id}`}
          />
          {hasErr && <p className="text-xs text-red-500 mt-1">{errs[field.id]}</p>}
        </div>
      );
    }

    if (fieldType === 'dropdown') {
      return (
        <div>
          <select
            className={inp(hasErr)}
            value={values[field.id] || ''}
            onChange={e => set(field.id, e.target.value)}
            disabled={disabled}
            data-testid={`reg-select-${field.id}`}
          >
            <option value="">Select…</option>
            {(field.options || []).filter(o => o.value?.trim()).map(o => (
              <option key={o.id} value={o.value}>{o.value}</option>
            ))}
          </select>
          {hasErr && <p className="text-xs text-red-500 mt-1">{errs[field.id]}</p>}
        </div>
      );
    }

    if (fieldType === 'radio') {
      return (
        <div>
          <div className="space-y-2">
            {(field.options || []).filter(o => o.value?.trim()).map(o => (
              <label key={o.id} className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="radio"
                  name={field.id}
                  value={o.value}
                  checked={values[field.id] === o.value}
                  onChange={() => set(field.id, o.value)}
                  className="accent-green-600 w-4 h-4 flex-shrink-0"
                  disabled={disabled}
                />
                <span className="text-sm text-gray-700">{o.value}</span>
              </label>
            ))}
          </div>
          {hasErr && <p className="text-xs text-red-500 mt-1">{errs[field.id]}</p>}
        </div>
      );
    }

    if (fieldType === 'checkbox') {
      return (
        <div>
          <div className="space-y-2">
            {(field.options || []).filter(o => o.value?.trim()).map(o => {
              const checked = Array.isArray(values[field.id]) && values[field.id].includes(o.value);
              return (
                <label key={o.id} className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={e => handleCheckbox(field.id, o.value, e.target.checked)}
                    className="w-4 h-4 accent-green-600 rounded flex-shrink-0"
                    disabled={disabled}
                  />
                  <span className="text-sm text-gray-700">{o.value}</span>
                </label>
              );
            })}
          </div>
          {hasErr && <p className="text-xs text-red-500 mt-1">{errs[field.id]}</p>}
        </div>
      );
    }

    if (fieldType === 'image_upload') {
      const uploadedUrl = values[field.id] || '';
      const isUp = uploading[field.id] || false;
      return (
        <div>
          {uploadedUrl ? (
            <div className="flex items-center gap-3">
              <img src={uploadedUrl} alt="Uploaded" className="h-16 w-16 rounded-xl object-cover border border-gray-200 flex-shrink-0" />
              <label className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-600 cursor-pointer hover:bg-gray-50 transition-colors">
                <Upload size={12} /> Change
                <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={e => handleImageUpload(field.id, e)} disabled={disabled} />
              </label>
              <button type="button" onClick={() => set(field.id, '')} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors">
                <X size={14} />
              </button>
            </div>
          ) : (
            <label className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border cursor-pointer transition-colors ${hasErr ? 'border-red-400 bg-red-50' : 'border-dashed border-gray-300 hover:bg-gray-50'} bg-white`}>
              {isUp ? (
                <span className="flex items-center gap-2 text-sm text-gray-500"><Loader size={14} className="animate-spin" /> Uploading…</span>
              ) : (
                <>
                  <Upload size={15} className="text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-500">Upload image · JPG, PNG, WebP · max 5 MB</span>
                </>
              )}
              <input type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={e => handleImageUpload(field.id, e)} disabled={disabled || isUp} />
            </label>
          )}
          {hasErr && <p className="text-xs text-red-500 mt-1">{errs[field.id]}</p>}
        </div>
      );
    }

    return null;
  };

  // ── Main render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-0" data-testid="karmana-reg-form">
        {/* Expired or capacity-full notice */}
        {registrationFull && (
          <div className="mb-3 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-600 font-semibold text-center" data-testid="registration-full-notice">
            Registration Is Closed.
          </div>
        )}
        {!registrationFull && disabled && (
          <div className="mb-3 px-3 py-2.5 rounded-lg bg-gray-100 border border-gray-200 text-xs text-gray-500 font-medium text-center">
            Registration is closed for this activity.
          </div>
        )}
        {/* ── Stacked label-above-input layout ── */}
        <div className="divide-y divide-gray-100">
          {fields.map((field) => {
            // Instruction: full-width rich text, no label
            if (field.type === 'instruction') {
              return (
                <div key={field.id} className="py-3" data-testid={`reg-field-${field.id}`}>
                  <SafeHtml
                    html={field.name || ''}
                    className="text-sm text-gray-700 prose prose-sm max-w-none leading-relaxed"
                  />
                  {field.instruction_image_url && (
                    <div className="mt-3 flex justify-center">
                      <img
                        src={field.instruction_image_url}
                        alt="Instruction"
                        className="max-h-64 rounded-lg border border-gray-200 object-contain"
                      />
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div key={field.id} className="py-3" data-testid={`reg-field-${field.id}`}>
                <p className="text-sm font-semibold text-gray-800 mb-2 leading-snug">
                  {field.name || 'Field'}
                  {field.mandatory && <span className="text-red-400 ml-0.5">*</span>}
                </p>
                {renderControl(field)}
              </div>
            );
          })}
        </div>

        {/* Contact error for anonymous */}
        {!user && touched['__contact'] && errs['__contact'] && (
          <p className="text-xs text-red-500 pt-1 pb-2">{errs['__contact']}</p>
        )}

        {/* Invisible reCAPTCHA container */}
        <div ref={recaptchaContainerRef} />

        {/* Separator */}
        <div className="h-px bg-gray-100 mb-4 mt-1" />

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={submitting || disabled}
          data-testid="karmana-reg-submit-btn"
          className="w-full py-3.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white text-sm font-bold disabled:opacity-50 transition-colors shadow-sm"
        >
          {submitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader size={15} className="animate-spin" />
              Submitting…
            </span>
          ) : (
            'Submit Registration'
          )}
        </button>

        {!user && (
          <p className="text-xs text-gray-400 text-center leading-relaxed pt-2">
            After submitting, you&apos;ll receive an OTP on email to verify your account.
          </p>
        )}
      </div>
  );
}
